"use client"

import { useTheme } from "next-themes"
import { Toaster as Sonner, ToasterProps, toast } from "sonner"
import { cn } from "@/lib/utils"

const Toaster = ({ className, ...props }: ToasterProps) => {
  const { theme = "system", forcedTheme } = useTheme()
  const sonnerTheme = (forcedTheme ?? theme) as ToasterProps["theme"]

  // 仅在客户端首次挂载时，为 success/error 设置默认时长
  if (typeof window !== "undefined" && !(window as any).__sonnerDurationsPatched) {
    try {
      const originalSuccess = toast.success.bind(toast)
      const originalError = toast.error.bind(toast)

      ;(toast as any).success = (
        message: React.ReactNode,
        options?: Parameters<typeof originalSuccess>[1]
      ) => originalSuccess(message, { duration: 3000, ...(options ?? {}) })

      ;(toast as any).error = (
        message: React.ReactNode,
        options?: Parameters<typeof originalError>[1]
      ) => originalError(message, { duration: 8000, ...(options ?? {}) })

      ;(window as any).__sonnerDurationsPatched = true
    } catch {}
  }

  return (
    <Sonner
      theme={sonnerTheme}
      position="top-center"
      closeButton
      expand
      offset={16}
      gap={12}
      toastOptions={{
        duration: 3000,
        classNames: {
          // 新式 UI：统一中性 popover 表面 + 大圆角 + 毛玻璃 + 柔和阴影；
          // 仅用「彩色图标 + 细描边」区分 error/success/info/warning，不再用整块红/绿底。
          toast: cn(
            "group flex items-center gap-3 rounded-2xl border border-border/60 bg-popover/90 px-4 py-3.5 text-popover-foreground shadow-2xl ring-1 ring-black/5 backdrop-blur-xl dark:ring-white/10",
            "[&_[data-icon]]:size-5 [&_[data-icon]]:shrink-0",
            "data-[type=error]:border-red-500/30 [&[data-type=error]_[data-icon]]:text-red-500",
            "data-[type=success]:border-emerald-500/30 [&[data-type=success]_[data-icon]]:text-emerald-500",
            "data-[type=info]:border-sky-500/30 [&[data-type=info]_[data-icon]]:text-sky-500",
            "data-[type=warning]:border-amber-500/30 [&[data-type=warning]_[data-icon]]:text-amber-500",
          ),
          title: "text-sm font-semibold leading-snug",
          description: "text-[13px] leading-snug text-muted-foreground",
          actionButton:
            "rounded-lg bg-foreground px-2.5 py-1 text-xs font-medium text-background",
          cancelButton:
            "rounded-lg bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground",
          closeButton:
            "rounded-full border-border/60 bg-popover text-muted-foreground hover:text-foreground",
        },
      }}
      className={cn("toaster group", className)}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "1rem",
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster }
